import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  Container,
  Fade,
  Skeleton,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const COLORS = {
  primary: "#123465",
  secondary: "#3b82f6",
  accent: "#f59e0b",
  lightBg: "#f8fafc",
  white: "#ffffff",
  border: "rgba(15, 23, 42, 0.08)",
  shadow: "rgba(15, 23, 42, 0.04)",
};

const getLogo = (x) => x?.logo || x?.Logo || x?.urlLogo || x?.image || "";
const getTitle = (x) => x?.titre || x?.title || x?.name || "Logo";

export default function LogoWallSlider({
  items = [],
  title = "Nos partenaires",
  highlight,
  subtitle,
  bg = COLORS.lightBg,
  emptyText = "Aucun logo à afficher pour le moment.",
  loading = false,

  // slider config
  autoplay = true,
  autoplaySpeed = 3000,
  pauseOnHover = true,

  // UI
  cardSize = { width: 176, height: 124 },
  gap = 20,
}) {
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up("sm"));
  const upMd = useMediaQuery(theme.breakpoints.up("md"));
  const upLg = useMediaQuery(theme.breakpoints.up("lg"));

  // ✅ Responsive slidesToShow
  const slidesToShow = upLg ? 6 : upMd ? 5 : upSm ? 3 : 2;

  const validItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items.filter((i) => !!getLogo(i));
  }, [items]);

  // Empty / loading
  const isEmpty = !loading && validItems.length === 0;

  // Build a repeated track so CSS can loop seamlessly without relying on a first-paint width guess.
  const track = useMemo(() => {
    if (!validItems.length) return [];
    return [...validItems, ...validItems, ...validItems];
  }, [validItems]);

  const len = validItems.length;

  // Pause
  const [paused, setPaused] = useState(false);

  const cycleDuration = Math.max(len, 1) * autoplaySpeed;
  const shouldAnimate = autoplay && len > slidesToShow;

  const onEnter = () => {
    if (pauseOnHover) {
      setPaused(true);
    }
  };

  const onLeave = () => {
    if (pauseOnHover) {
      setPaused(false);
    }
  };

  const [animationReady, setAnimationReady] = useState(false);

  useEffect(() => {
    setAnimationReady(false);
    if (!shouldAnimate) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setAnimationReady(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [shouldAnimate, len]);

  const LoadingSkeleton = () => (
    <Box sx={{ display: "flex", gap: `${gap}px`, overflow: "hidden", justifyContent: "center" }}>
      {Array.from({ length: slidesToShow }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width={cardSize.width}
          height={cardSize.height}
          sx={{
            borderRadius: 2.5,
            flexShrink: 0,
            bgcolor: "rgba(15, 23, 42, 0.06)",
          }}
          animation="wave"
        />
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        py: { xs: 8, md: 11 },
        bgcolor: bg,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: `linear-gradient(90deg, transparent, ${COLORS.border} 50%, transparent)`,
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Fade in appear={false} timeout={700}>
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Typography
              component="h2"
              sx={{
                color: COLORS.primary,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
                mb: 1.5,
                position: "relative",
                display: "inline-block",
                "&::after": highlight
                  ? {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: COLORS.accent,
                    borderRadius: "2px",
                  }
                  : {},
              }}
            >
              {title}{" "}
              {highlight && (
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.accent})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {highlight}
                </Box>
              )}
            </Typography>

            {subtitle && (
              <Typography
                sx={{
                  color: "rgba(15,23,42,0.72)",
                  fontSize: { xs: "1rem", md: "1.06rem" },
                  lineHeight: 1.8,
                  maxWidth: 860,
                  mx: "auto",
                  fontWeight: 650,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Fade>

        {/* Content Section */}
        {loading ? (
          <LoadingSkeleton />
        ) : isEmpty ? (
          <Fade in appear={false} timeout={500}>
            <Box
              sx={{
                bgcolor: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 3,
                p: { xs: 3, md: 5 },
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: COLORS.primary,
                  fontSize: { xs: 16, md: 18 },
                  mb: 1,
                }}
              >
                {emptyText}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(15, 23, 42, 0.5)",
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 400,
                }}
              >
                Vérifie que chaque item contient un champ logo valide.
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Box
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2, md: 3 },
              py: 2,
            }}
          >
            {/* Gradient Fade Edges */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: { xs: 50, md: 80 },
                zIndex: 2,
                background: `linear-gradient(90deg, ${bg} 0%, transparent 100%)`,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: { xs: 50, md: 80 },
                zIndex: 2,
                background: `linear-gradient(270deg, ${bg} 0%, transparent 100%)`,
                pointerEvents: "none",
              }}
            />

            {/* Slider Track */}
            <Box
              sx={{
                display: "flex",
                gap: `${gap}px`,
                width: "max-content",
                transform: "translate3d(0,0,0)",
                willChange: "transform",
                animation: shouldAnimate && animationReady ? `logoWallMarquee ${cycleDuration}ms linear infinite` : "none",
                animationPlayState: paused ? "paused" : "running",
                "@keyframes logoWallMarquee": {
                  from: {
                    transform: "translate3d(0,0,0)",
                  },
                  to: {
                    transform: "translate3d(-33.3333%,0,0)",
                  },
                },
              }}
            >
              {track.map((item, i) => (
                <Card
                  key={`${getTitle(item)}-${i}`}
                  elevation={0}
                  sx={{
                    width: cardSize.width,
                    height: cardSize.height,
                    flexShrink: 0,
                    borderRadius: 2.5,
                    bgcolor: COLORS.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1.5,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-8px) scale(1.02)",
                      boxShadow: "0 12px 32px rgba(59, 130, 246, 0.15)",
                      "&::before": {
                        opacity: 1,
                      },
                    },
                    "&:active": {
                      transform: "translateY(-6px) scale(1.01)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={getLogo(item)}
                    alt={getTitle(item)}
                    sx={{
                      width: "100%",
                      height: "100%",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      opacity: 0.75,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      zIndex: 1,
                      ".MuiCard-root:hover &": {
                        filter: "grayscale(0%) brightness(1)",
                        opacity: 1,
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}
